import React from "react";

export interface WidgetErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered in place of the children when they throw during render. */
  fallback?: React.ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
}

/**
 * Isolates a single assistant widget so a render error in it cannot unmount the whole
 * application. Widget payloads (especially approval inputs) are model-generated and may
 * arrive partial or off-shape; without this boundary a single throwing widget blanked the
 * entire backoffice page. The error is logged to the console so it stays debuggable.
 */
export class WidgetErrorBoundary extends React.Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  state: WidgetErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): WidgetErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown): void {
    // eslint-disable-next-line no-console
    console.error("[ai-assistant] widget render error", error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
